import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { PublicShell } from "@/public/PublicChrome";
import { Section, Eyebrow, Lede, Reveal, Disclaimer, RelatedLinks } from "@/public/ui";
import { SEO } from "@/public/seo";
import { mdToHtml } from "@/public/md";
import MarketLimitArticle from "@/public/pages/MarketLimitArticle";

const API = process.env.REACT_APP_BACKEND_URL || "";

// ------------------------------------------------------------------ Insights index (editorial layer)
export function InsightsIndex() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/organic/insights`).then((r) => r.json()).then((d) => setItems(d.insights || [])).catch(() => setItems([]));
  }, []);
  return (
    <PublicShell>
      <Seo {...SEO.insights} noindex={false} />
      <Section className="pt-32 sm:pt-40 pb-10">
        <div className="absolute inset-x-0 top-0 -z-10 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(6,182,212,0.10),transparent_65%)]" aria-hidden="true" />
        <Reveal><Eyebrow>Insights</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.08] text-tc-txt">
            Checking and challenging AI market reads.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <Lede className="mt-6 max-w-2xl">
            First-party methodology and analysis on verifying AI crypto and technical-analysis reads against
            computed market evidence — deterministic evidence separated from interpretation, with conflict,
            invalidation and PASS treated as legitimate conclusions.
          </Lede>
        </Reveal>
      </Section>

      <Section className="pb-16">
        <div className="border-t border-white/8 pt-10" data-testid="insights-list">
          {items === null && <p className="text-tc-muted text-sm">Loading…</p>}
          {items && items.length === 0 && (
            <p className="text-tc-muted text-sm" data-testid="insights-empty">
              No published insights yet. New resources are researched, verified and reviewed before publication.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
            {(items || []).map((r) => (
              <Reveal key={r.slug} as="div">
                <Link to={`/insights/${r.slug}`} data-testid={`insight-card-${r.slug}`} className="block group">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-tc-coach">{r.topic_cluster}</div>
                  <h2 className="mt-2 font-heading text-lg font-semibold text-tc-txt group-hover:text-tc-coach transition-colors">{r.title}</h2>
                  <p className="mt-1.5 text-sm text-tc-sub leading-relaxed">{r.dek || r.meta_description}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
        <Disclaimer className="mt-10">
          Educational analysis of methodology and market concepts. Not financial advice; no real-money
          execution; no guaranteed outcomes.
        </Disclaimer>
      </Section>

      <RelatedLinks links={[["Glossary", "/glossary"], ["Intelligence", "/intelligence"], ["Analyst", "/analyst"], ["How It Works", "/how-it-works"]]} />
    </PublicShell>
  );
}

// ------------------------------------------------------------------ Insight article
export function InsightArticle() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, article: null });
  useEffect(() => {
    fetch(`${API}/api/organic/insights/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((a) => setState({ loading: false, article: a }))
      .catch(() => setState({ loading: false, article: null }));
  }, [slug]);

  if (state.loading) return <PublicShell><Section className="pt-40 pb-24"><p className="text-tc-muted text-sm">Loading…</p></Section></PublicShell>;

  if (!state.article) {
    return (
      <PublicShell>
        <Seo title="Not found" path={`/insights/${slug}`} noindex={true} noCanonical={true} />
        <Section className="pt-40 pb-24 text-center">
          <h1 className="font-heading text-3xl font-bold text-tc-txt">This insight isn't available.</h1>
          <p className="mt-3 text-tc-sub">It may be unpublished or the link is incorrect.</p>
          <Link to="/insights" className="mt-6 inline-block text-tc-coach hover:underline" data-testid="insight-back">← All insights</Link>
        </Section>
      </PublicShell>
    );
  }

  const a = state.article;
  const base = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${base}/insights/${a.slug}`;
  // Author/publisher reference the sitewide Organization @id (see seo.js ORG) rather than
  // inlining a new person byline — matches the existing site-wide schema pattern.
  const ORG_REF = { "@id": "https://tradecraftonline.com/#organization" };
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article", "@id": `${url}#article`, headline: a.title,
        description: a.meta_description, url, inLanguage: "en-US",
        author: ORG_REF,
        publisher: ORG_REF,
        datePublished: a.published_at, dateModified: a.updated_content_at || a.published_at,
        about: a.entities || undefined,
      },
      ...(a.faq && a.faq.length ? [{
        "@type": "FAQPage", "@id": `${url}#faq`,
        mainEntity: a.faq.map(([q, ans]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: ans } })),
      }] : []),
      {
        "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
        itemListElement: [["Home", "/"], ["Insights", "/insights"], [a.title, `/insights/${a.slug}`]].map(([n, p], i) => ({ "@type": "ListItem", position: i + 1, name: n, item: `${base}${p}` })),
      },
    ],
  };

  // Slug-specific layouts: Article 1 uses the redesigned long-form reading template.
  if (a.slug === "market-order-vs-limit-order") {
    return (
      <PublicShell>
        <Seo title={a.meta_title || a.title} description={a.meta_description} path={`/insights/${a.slug}`} noindex={false} type="article" jsonLd={jsonLd} />
        <MarketLimitArticle article={a} />
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      <Seo title={a.meta_title || a.title} description={a.meta_description} path={`/insights/${a.slug}`} noindex={false} type="article" jsonLd={jsonLd} />
      <Section className="pt-32 sm:pt-40 pb-16 max-w-3xl">
        <Reveal><div className="font-mono text-[11px] uppercase tracking-widest text-tc-coach" data-testid="insight-cluster">{a.topic_cluster}</div></Reveal>
        <Reveal delay={0.05}>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1] text-tc-txt" data-testid="insight-title">{a.title}</h1>
        </Reveal>
        {a.dek && <Reveal delay={0.1}><Lede className="mt-4">{a.dek}</Lede></Reveal>}
        {a.answer_summary && (
          <Reveal delay={0.12}>
            <div className="mt-6 rounded-xl border border-tc-coach/30 bg-tc-coach/[0.06] p-5" data-testid="insight-answer">
              <div className="font-mono text-[10px] uppercase tracking-widest text-tc-coach mb-2">In short</div>
              <p className="text-sm text-tc-txt leading-relaxed">{a.answer_summary}</p>
            </div>
          </Reveal>
        )}
        <article className="tc-prose mt-8 text-tc-sub leading-relaxed space-y-4" data-testid="insight-body"
          dangerouslySetInnerHTML={{ __html: mdToHtml(a.body_markdown) }} />

        {a.key_takeaways?.length > 0 && (
          <div className="mt-10 border-t border-white/8 pt-6">
            <h2 className="font-heading text-lg font-semibold text-tc-txt">Key takeaways</h2>
            <ul className="mt-3 space-y-2 text-sm text-tc-sub list-disc pl-5">
              {a.key_takeaways.map((k, i) => <li key={i}>{k}</li>)}
            </ul>
          </div>
        )}

        {a.faq?.length > 0 && (
          <div className="mt-10 border-t border-white/8 pt-6">
            <h2 className="font-heading text-lg font-semibold text-tc-txt">FAQ</h2>
            <dl className="mt-3 space-y-4">
              {a.faq.map(([q, ans], i) => (
                <div key={i}><dt className="font-semibold text-tc-txt text-sm">{q}</dt><dd className="mt-1 text-sm text-tc-sub">{ans}</dd></div>
              ))}
            </dl>
          </div>
        )}

        {a.citations?.length > 0 && (
          <div className="mt-10 border-t border-white/8 pt-6">
            <h2 className="font-heading text-lg font-semibold text-tc-txt">Sources</h2>
            <ul className="mt-3 space-y-1.5 text-sm">
              {a.citations.map((c, i) => {
                const [curl, title] = Array.isArray(c) ? c : [c?.url || c, c?.title];
                return <li key={i}><a href={curl} target="_blank" rel="nofollow noopener" className="text-tc-coach hover:underline break-words">{title || curl}</a></li>;
              })}
            </ul>
          </div>
        )}

        {a.related_glossary_terms?.length > 0 && (
          <p className="mt-8 text-sm text-tc-muted">
            Related terms: {a.related_glossary_terms.map((t, i) => (
              <React.Fragment key={t}>{i > 0 ? ", " : ""}<Link to="/glossary" className="text-tc-coach hover:underline">{t}</Link></React.Fragment>
            ))}
          </p>
        )}

        <Disclaimer className="mt-10">
          Educational analysis. Not financial advice; no real-money execution; no guaranteed outcomes.
          Cited sources require independent review.
        </Disclaimer>
        <Link to="/insights" className="mt-8 inline-block text-tc-coach hover:underline" data-testid="insight-back">← All insights</Link>
      </Section>
    </PublicShell>
  );
}
